import React, { useEffect, useRef, useState, Suspense } from 'react';
import ReactDOM from 'react-dom';
import Vue from 'vue';
import { createBrowserHistory } from 'history';
import { Router, Switch, Redirect, Route } from 'react-router-dom';

const NoVueEnv = () => {
    return (React.createElement("div", { style: { color: 'gray' } }, "No Vue environment detected, please open it in the Vue env application."));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var createRoot;

var m = ReactDOM;
{
  createRoot = m.createRoot;
  m.hydrateRoot;
}

function getAppCtx() {
    return window.APP_CONTEXT;
}
const getAppCtxProxy = () => {
    const debugWarn = (p, target) => {
        console.debug(`[React Runtime] No Vue context runtime found, please check the key name is: "${p}" in target object:`, target);
    };
    const context = {};
    return new Proxy(context, {
        get(_, p, receiver) {
            const ctx = getAppCtx();
            if (!ctx || typeof ctx !== 'object')
                return undefined;
            const res = Reflect.get(ctx, p, receiver);
            if (!res) {
                debugWarn(p, ctx);
                return undefined;
            }
            return res;
        },
    });
};
const AppCtx = getAppCtxProxy();
const IN_ADMIN_CONTEXT = !!getAppCtx();

const ComponentWithCallback = (props) => {
    useEffect(() => {
        var _a;
        (_a = props.callback) === null || _a === void 0 ? void 0 : _a.call(props);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return React.createElement(React.Fragment, null, props.children);
};

var shim = {exports: {}};

var useSyncExternalStoreShim_production_min = {};

/**
 * @license React
 * use-sync-external-store-shim.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredUseSyncExternalStoreShim_production_min;

function requireUseSyncExternalStoreShim_production_min () {
	if (hasRequiredUseSyncExternalStoreShim_production_min) return useSyncExternalStoreShim_production_min;
	hasRequiredUseSyncExternalStoreShim_production_min = 1;
var e=React;function h(a,b){return a===b&&(0!==a||1/a===1/b)||a!==a&&b!==b}var k="function"===typeof Object.is?Object.is:h,l=e.useState,m=e.useEffect,n=e.useLayoutEffect,p=e.useDebugValue;function q(a,b){var d=b(),f=l({inst:{value:d,getSnapshot:b}}),c=f[0].inst,g=f[1];n(function(){c.value=d;c.getSnapshot=b;r(c)&&g({inst:c});},[a,d,b]);m(function(){r(c)&&g({inst:c});return a(function(){r(c)&&g({inst:c});})},[a]);p(d);return d}
	function r(a){var b=a.getSnapshot;a=a.value;try{var d=b();return !k(a,d)}catch(f){return !0}}function t(a,b){return b()}var u="undefined"===typeof window||"undefined"===typeof window.document||"undefined"===typeof window.document.createElement?t:q;useSyncExternalStoreShim_production_min.useSyncExternalStore=void 0!==e.useSyncExternalStore?e.useSyncExternalStore:u;
	return useSyncExternalStoreShim_production_min;
}

{
  shim.exports = requireUseSyncExternalStoreShim_production_min();
}

var shimExports = shim.exports;

let vueInstance = undefined;
try {
    vueInstance = 'Vue' in window ? new window.Vue() : undefined;
}
catch (error) {
    console.error(error);
}
const getPropValue = (options) => {
    const { prop, type, store = AppCtx.store } = options;
    if (!store)
        return undefined;
    const propArr = prop.split('.');
    return propArr.reduce((result, prop) => {
        return result[prop];
    }, type ? store[type] : store);
};
const watch = (options) => {
    const { cb, instance = vueInstance, deep } = options;
    if (instance && typeof (instance === null || instance === void 0 ? void 0 : instance.$watch) === 'function') {
        return instance.$watch(() => getPropValue(options), cb, { deep });
    }
    console.error('[React Runtime] No Vue context runtime found, please check the key name is: "$watch" in object:', instance);
    return () => { };
};
const useVueData = (options) => {
    const { prop, type, store, instance, deep } = options || {};
    return shimExports.useSyncExternalStore((listener) => {
        return watch({ prop, cb: listener, type, store, instance: instance || store, deep });
    }, () => {
        return getPropValue({ prop, type, store });
    });
};
const useVuexState = (prop) => {
    return shimExports.useSyncExternalStore((listener) => {
        return watch({ prop, cb: listener, type: 'state' });
    }, () => {
        return getPropValue({ prop, type: 'state' });
    });
};
const useVuexGetters = (prop) => {
    return shimExports.useSyncExternalStore((listener) => {
        return watch({ prop, cb: listener, type: 'getters' });
    }, () => {
        return getPropValue({ prop, type: 'getters' });
    });
};
const useVuexDispatch = (_vueStore) => {
    return (...args) => {
        (_vueStore || AppCtx.store).dispatch(...args);
    };
};

const isVueElement = (element) => {
    return ((typeof element === 'function' &&
        element.prototype &&
        element.prototype.constructor &&
        element.prototype.constructor.super &&
        element.prototype.constructor.super.isVue) ||
        element instanceof Vue ||
        (typeof element === 'object' &&
            ((typeof element.render === 'function' && typeof element.staticRenderFns === 'object') ||
                element._isVue === true)));
};
const isComponentClass = (func) => {
    var _a, _b;
    return typeof func === 'function' &&
        ((typeof ((_a = func.prototype) === null || _a === void 0 ? void 0 : _a.render) === 'function' && !!((_b = func.prototype) === null || _b === void 0 ? void 0 : _b.isReactComponent) === true) ||
            func.prototype instanceof React.Component);
};
const getReactComponentType = (component) => {
    // react function component type
    //   Symbol.for('react.memo'),
    //   Symbol.for('react.lazy'),
    //   Symbol.for('react.forward_ref'),
    //   Symbol.for('react.suspense'),
    // other types
    //   Symbol.for('react.element'),
    //   Symbol.for('react.portal'),
    //   Symbol.for('react.profiler'),
    //   Symbol.for('react.scope'),
    //   Symbol.for('react.provider'),
    //   Symbol.for('react.context'),
    //   React 18 types
    //   Symbol.for('react.memo.exotic'),
    //   Symbol.for('react.fundamental'),
    //   Symbol.for('react.block'),
    //   Symbol.for('react.server.block'),
    //   Symbol.for('react.server.block.suspense'),
    return component.$$typeof.toString().replace(/(Symbol|\(|\))/g, '');
};
const isReactFunctionComponent = (component) => {
    try {
        if (typeof component !== 'object' || component === null || Array.isArray(component)) {
            return false;
        }
        const reactFunctionComponentTypes = [
            Symbol.for('react.memo'),
            Symbol.for('react.lazy'),
            Symbol.for('react.forward_ref'),
            Symbol.for('react.suspense'),
        ];
        return reactFunctionComponentTypes.some((type) => component.$$typeof === type);
    }
    catch (error) {
        console.error('[Micro]:', error);
        return false;
    }
};
const NullComponent = () => null;
const getValidComponent = (element) => {
    if (React.isValidElement(element)) {
        return () => React.createElement(React.Fragment, null, element);
    }
    else {
        console.error('[Micro]: Element is not a valid React Element, the type is:', getReactComponentType(element));
    }
    return NullComponent;
};
const getWrapPropReactComponent = (element) => {
    if (element instanceof Promise) {
        return React.lazy(() => __awaiter(void 0, void 0, void 0, function* () { return yield element; }));
    }
    if (isComponentClass(element))
        return element;
    if (isReactFunctionComponent(element))
        return element;
    return getValidComponent(element);
};
function getReactRouterComponent(component) {
    try {
        if (typeof component === 'function') {
            try {
                const Component = component({});
                if (typeof Component === 'function')
                    return Component;
                return getWrapPropReactComponent(Component);
            }
            catch (error) {
                return component;
            }
        }
        return getWrapPropReactComponent(component);
    }
    catch (error) {
        const ERROR_TAG = `__ERROR_UTILS_${Date.now()}__`;
        window[ERROR_TAG] = {
            getReactRouterComponent,
            isComponentClass,
            isReactFunctionComponent,
            getValidComponent,
            options: { component },
        };
        console.error(error, `[Micro GRFC]: Get react component failed, please check the input parameters component: ${component}. utils ${ERROR_TAG} have been integrated into window`);
        return NullComponent;
    }
}
const satisfies = (v1, v2) => {
    var _a;
    if ((_a = v1.split('.')) === null || _a === void 0 ? void 0 : _a[0].includes(v2)) {
        return true;
    }
    return false;
};
const ReactRoot = (props) => {
    const { Component, store, deep = true, inAdminContext = IN_ADMIN_CONTEXT } = props, rest = __rest(props, ["Component", "store", "deep", "inAdminContext"]);
    const data = useVueData({ prop: 'data', store, deep });
    return React.createElement(React.Fragment, null, React.createElement(Component, Object.assign({}, rest, { data: data, IS_IN_ADMIN: inAdminContext })));
};
const ReactRender = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const { el, store, callback } = options;
    const Component = options.component || options.Component;
    const container = typeof el === 'string' ? document.querySelector(el) : el;
    if (!container) {
        throw new Error(`[Render] document.querySelector(${el}) or ${el} is not assignable to parameter of type 'Element | DocumentFragment'`);
    }
    if (satisfies(React.version, '17')) {
        const app = typeof Component === 'function' ? (React.createElement(ReactRoot, { Component: Component, store: store })) : (Component);
        if (!app)
            throw new Error('There must be an App node to execute ReactDOM.render');
        ReactDOM.render(app, container, callback);
        return Object.assign(Object.assign({}, app), { unmountCom: () => {
                ReactDOM.unmountComponentAtNode(container);
            } });
    }
    else if (satisfies(React.version, '18')) {
        const app = typeof Component === 'function' ? (React.createElement(ComponentWithCallback, { callback: callback },
            React.createElement(ReactRoot, { Component: Component, store: store }))) : (Component);
        const root = createRoot(container);
        root.render(app);
        return Object.assign(Object.assign({}, root), { unmountCom: () => {
                root.unmount();
            } });
    }
});

function generateUUIDv4() {
    const randomBytesArray = generateRandomBytes(16);
    const randomBytesString = Array.from(randomBytesArray)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    return uuid.replace('00000000', randomBytesString.substring(0, 8));
}
function generateRandomBytes(length) {
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
}
const uuid = generateUUIDv4;

const VUE_IN_REACT_COMPONENT_NAME = 'react-vue-component-wrapper';
const VueInReactComponent = (props) => {
    const { __unique__id__, component, on, vueRouter, vueStore, vueI18n } = props, rest = __rest(props, ["__unique__id__", "component", "on", "vueRouter", "vueStore", "vueI18n"]);
    const vueInstance = useRef();
    const createVueInstance = () => {
        var _a, _b;
        if (!Vue) {
            console.error('No Vue environment detected, please open it in the fms admin application.');
            return;
        }
        vueInstance.current = new Vue({
            data: rest,
            router: vueRouter || AppCtx.router,
            store: vueStore || AppCtx.store,
            i18n: vueI18n || AppCtx.i18n,
            render(c) {
                return c(VUE_IN_REACT_COMPONENT_NAME, {
                    props: this.$data,
                    on,
                }, null);
            },
            components: {
                [VUE_IN_REACT_COMPONENT_NAME]: component,
            },
        }).$mount((_b = (_a = document.getElementById(__unique__id__)) === null || _a === void 0 ? void 0 : _a.childNodes) === null || _b === void 0 ? void 0 : _b[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };
    useEffect(() => () => { var _a; return (_a = vueInstance.current) === null || _a === void 0 ? void 0 : _a.$destroy(); }, []);
    useEffect(() => {
        if (vueInstance.current) {
            Object.assign(vueInstance.current.$data, props);
            vueInstance.current.$forceUpdate();
        }
    }, [props]);
    useEffect(() => {
        var _a;
        vueInstance.current &&
            (vueInstance.current.$options.components[VUE_IN_REACT_COMPONENT_NAME] = component);
        (_a = vueInstance.current) === null || _a === void 0 ? void 0 : _a.$forceUpdate();
    }, [component]);
    useEffect(() => {
        createVueInstance();
    });
    if (!Vue) {
        return React.createElement(NoVueEnv, null);
    }
    return (React.createElement("div", { id: __unique__id__ },
        React.createElement("div", null)));
};
/**
 * 在 React 当中使用 Vue 组件
 * @param option Vue 组件
 * @returns React 组件
 */
const VueAdapter = (option) => {
    try {
        const __unique__id__ = uuid();
        if (isVueElement(option)) {
            return (props) => (React.createElement(VueInReactComponent, Object.assign({}, Object.assign(Object.assign({}, props), { __unique__id__ }), { component: option })));
        }
        return option;
    }
    catch (error) {
        const ERROR_TAG = `__ERROR_UTILS_${Date.now()}__`;
        console.error(error, `[Vue Adapter] Initialization failed, please check the options. utils ${ERROR_TAG} have been integrated into window.`);
        window[ERROR_TAG] = { VueAdapter, isVueElement, option };
    }
};

const addLeadingSlash = (path, slash = '/') => {
    return path ? (path.charAt(0) === slash ? path : slash + path) : slash;
};
const addLeadingBasename = (subPath, basename = '/') => {
    const slashBasename = addLeadingSlash(basename);
    const path = addLeadingSlash(subPath);
    return path.startsWith(slashBasename) ? path : slashBasename + path;
};

const promptPathNil = (route) => {
    if (!route.path) {
        console.error('[React Routes Init] Note that path is an empty value, please make sure it is within your expectations!', route);
    }
};

const generateRouteElements = (routes, basename) => {
    const generateRouteElement = (item, index, basename) => {
        promptPathNil(item);
        const res = { redirect: undefined, element: undefined };
        if (!item.component) {
            return res;
        }
        const Component = getReactRouterComponent(item.component);
        const path = addLeadingBasename(item.path, basename);
        if (item.redirect) {
            res.redirect = (React.createElement(Redirect, { key: `${path}-${index}-redirect`, exact: true, from: path, to: item.redirect }));
        }
        const children = item.children &&
            generateRouteElements(item.children.map((child) => (Object.assign(Object.assign({}, child), { path: path + addLeadingSlash(child === null || child === void 0 ? void 0 : child.path) }))), basename);
        res.element = (React.createElement(Route, { exact: !item.children || item.children.length === 0, path: path, key: `${path}-${index}`, render: (props) => (React.createElement(Suspense, { fallback: item.fallback },
                React.createElement(Component, Object.assign({}, props), children))) }));
        return res;
    };
    const routeElements = [];
    const redirects = [];
    routes.forEach((item, index) => {
        const { redirect, element } = generateRouteElement(item, index, basename);
        redirect && redirects.push(redirect);
        element && routeElements.push(element);
    });
    return redirects.concat(routeElements);
};
const EnhancedNestedRoutes = (props) => {
    const { history, routes: _routes, basename = '/', mergeSamePathChildren, callbackInternalSetRoutes, fallback, } = props;
    const [routes, setRoutes] = useState(_routes);
    useEffect(() => {
        callbackInternalSetRoutes === null || callbackInternalSetRoutes === void 0 ? void 0 : callbackInternalSetRoutes(setRoutes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const processedRoutes = mergeSamePathChildren
        ? (() => {
            const map = new Map();
            routes.forEach((item) => {
                const path = addLeadingBasename(item.path, basename);
                if (!map.has(path)) {
                    map.set(path, item);
                }
                else {
                    map.set(path, Object.assign(Object.assign({}, map.get(path)), { children: map.get(path).children.concat(item.children) }));
                }
            });
            return Array.from(map.values());
        })()
        : routes;
    const routeElements = generateRouteElements(processedRoutes, basename);
    return (React.createElement(Router, { history: history },
        React.createElement(Suspense, { fallback: fallback },
            React.createElement(Switch, null, routeElements))));
};

const Reactive = (props) => {
    return (React.createElement("div", null,
        React.createElement(Switch, null, props === null || props === void 0 ? void 0 : props.children)));
};

const Routes = EnhancedNestedRoutes;
const reactRoutesGenerator = (routes, history, basename) => React.createElement(Routes, { routes: routes, history: history, basename: basename });
class ReactRouterFactory {
    constructor(options) {
        this.hash = typeof options.hash !== 'undefined' ? options.hash : true;
        this.logger = options.logger || false;
        this.uniquename = options.uniquename;
        this.basename = options.basename;
        this.routeModules = options.routeModules;
        this.mergeSamePathChildren =
            typeof options.mergeSamePathChildren !== 'undefined' ? options.mergeSamePathChildren : true;
        this.fallback = options.fallback;
    }
    get SYNC_REACT_REPLACE_STATE_KEY() {
        var _a;
        return `__${(_a = this.uniquename) === null || _a === void 0 ? void 0 : _a.toUpperCase()}_SYNC_REACT_REPLACE_STATE__`;
    }
    set syncReactReplaceState(value) {
        if (!this.basename)
            return;
        window[this.SYNC_REACT_REPLACE_STATE_KEY] = value;
    }
    get syncReactReplaceState() {
        if (!this.basename)
            return false;
        return window[this.SYNC_REACT_REPLACE_STATE_KEY];
    }
    getCurrentPathname() {
        if (window.location.pathname !== '/') {
            return window.location.pathname;
        }
        else if (window.location.hash !== '') {
            return window.location.hash.slice(1);
        }
        else {
            return '/';
        }
    }
    get history() {
        if (!this.historyInstance) {
            const self = this;
            const APP_ROUTE_BASENAME = this.basename;
            const logger = this.logger;
            const hash = this.hash;
            const vueRouter = this.vueRouter || AppCtx.router;
            const inAdminContext = typeof this.inAdminContext === 'boolean' ? this.inAdminContext : IN_ADMIN_CONTEXT;
            this.historyInstance = new Proxy(createBrowserHistory({ basename: hash ? '/#/' : '' }), {
                get(target, propKey, receiver) {
                    if (propKey === 'push' || propKey === 'replace') {
                        return (...args) => {
                            var _a;
                            const [path] = args;
                            if (typeof path === 'string') {
                                if (inAdminContext && self.syncReactReplaceState) {
                                    self.syncReactReplaceState = false;
                                    return;
                                }
                                const prePath = self.getCurrentPathname();
                                const isReactPage = path.startsWith(addLeadingSlash(APP_ROUTE_BASENAME));
                                if (isReactPage) {
                                    Reflect.get(target, propKey, receiver).apply(this, args);
                                }
                                if (path === prePath)
                                    return;
                                if (logger) {
                                    console.debug('[React Router], exec vue router');
                                }
                                self.syncReactReplaceState = true;
                                // If this route is not registered in vue, the closed loop will not be formed
                                return !isReactPage ? vueRouter[propKey](...args) : (_a = vueRouter === null || vueRouter === void 0 ? void 0 : vueRouter.replace) === null || _a === void 0 ? void 0 : _a.call(vueRouter, ...args);
                            }
                            return Reflect.get(target, propKey, receiver).apply(this, args);
                        };
                    }
                    return Reflect.get(target, propKey, receiver);
                },
            });
        }
        return this.historyInstance;
    }
    get routes() {
        var _a;
        if (this._routes) {
            return this._routes;
        }
        else {
            const _routes = (_a = this.routeModules) === null || _a === void 0 ? void 0 : _a.map((config) => {
                if (typeof config === 'function') {
                    const res = config();
                    if (res.then && typeof res.then === 'function') ;
                    return Object.assign({ isReact: true }, res);
                }
                return Object.assign({ isReact: true }, config);
            });
            this._routes = _routes;
            return _routes;
        }
    }
    addRoutes(routes, perAddCallback) {
        var _a;
        routes.forEach((route) => {
            this.routes.push(route);
            perAddCallback === null || perAddCallback === void 0 ? void 0 : perAddCallback(route);
        });
        (_a = this.internalSetRoutes) === null || _a === void 0 ? void 0 : _a.call(this, this.routes);
    }
    addRoute(route) {
        var _a;
        this.routes.push(route);
        (_a = this.internalSetRoutes) === null || _a === void 0 ? void 0 : _a.call(this, this.routes);
    }
    get Routes() {
        return () => (React.createElement(EnhancedNestedRoutes, { routes: this.routes, history: this.history, basename: this.basename, mergeSamePathChildren: this.mergeSamePathChildren, callbackInternalSetRoutes: (setRoutes) => {
                this.internalSetRoutes = setRoutes;
            }, fallback: this.fallback }));
    }
}

const ReactInVueComponent = (props) => {
    const __unique__id__ = uuid();
    const { name, id = __unique__id__, Component, history } = props;
    return {
        _isReact: true,
        name,
        props: ['data', 'deep'],
        data() {
            return {
                id,
                isMounted: false,
            };
        },
        mounted() {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                const vm = this;
                const el = vm.$refs[vm.id];
                const callback = () => {
                    vm.isMounted = true;
                };
                if (el && Component) {
                    const root = yield ReactRender({ el, Component, callback, store: vm });
                    (_a = vm.$route) === null || _a === void 0 ? void 0 : _a.fullPath;
                    vm.$once('hook:beforeDestroy', () => {
                        // Warning: Attempted to synchronously unmount a root while React was already rendering. React cannot finish unmounting the root until the current render has completed, which may lead to a race condition.
                        // reference: https://stackoverflow.com/questions/73459382/react-18-async-way-to-unmount-root
                        setTimeout(() => {
                            root === null || root === void 0 ? void 0 : root.unmountCom();
                            vm.isMounted = false;
                        }, 0);
                    });
                    vm.$watch('$route', (route) => {
                        var _a;
                        (_a = history === null || history === void 0 ? void 0 : history.replace) === null || _a === void 0 ? void 0 : _a.call(history, route.fullPath);
                    }, {
                        immediate: true,
                    });
                }
                else {
                    console.error('[React Adapter] please check component node:', Component);
                }
            });
        },
        render(createElement) {
            const vm = this;
            return createElement('div', { attrs: { id: vm.id }, ref: vm.id }, !vm.isMounted
                ? [
                    createElement('div', {
                        directives: {
                            loading: true,
                        },
                        style: {
                            height: 'calc(100vh - 56px)',
                        },
                    }),
                ]
                : undefined);
        },
    };
};
/**
 * 在 Vue 中使用 React 组件
 * @param options React 组件或包含了 React 组件 (options.Component) 的对象
 * @returns Vue 组件
 */
const ReactAdapter = (options) => {
    try {
        const isMix = typeof options === 'object' && 'Component' in options;
        const Component = isMix ? options.Component || options.component : options;
        const props = isMix ? options : { Component: options };
        return !isVueElement(Component) ? ReactInVueComponent(props) : Component;
    }
    catch (error) {
        const ERROR_TAG = `__ERROR_UTILS_${Date.now()}__`;
        console.error(error, `[React Adapter] Initialization failed, please check the options. utils ${ERROR_TAG} have been integrated into window`);
        window[ERROR_TAG] = { ReactAdapter, isVueElement, ReactInVueComponent, options };
    }
};

const VueEmpty = {
    render(createElement) {
        return createElement('router-view');
    },
};

class RouterFactory {
    constructor(options) {
        Object.assign(this, options);
    }
    get pager() {
        return ReactAdapter({
            Component: this.rootGenerator(this.ReactRoutes),
            id: this.uniquename,
            history: this.history,
            type: 'page',
        });
    }
    rootGenerator(Routes) {
        const App = this.App;
        return (props) => (React.createElement(App, Object.assign({}, props),
            React.createElement(Routes, null)));
    }
    loopRoutes(configs) {
        const history = this.history;
        return configs === null || configs === void 0 ? void 0 : configs.map((config) => {
            var _a;
            return Object.assign(Object.assign({}, config), { component: VueEmpty, children: (_a = config.children) === null || _a === void 0 ? void 0 : _a.map((child) => {
                    const Component = this.rootGenerator(() => reactRoutesGenerator([Object.assign(Object.assign({}, config), { children: [child] })], history));
                    return Object.assign(Object.assign({}, child), { component: ReactAdapter({
                            Component: (props) => React.createElement(Component, Object.assign({}, props)),
                            history,
                            type: 'page',
                        }), children: this.loopRoutes(child.children) });
                }) });
        });
    }
    get routes() {
        const basename = this.basename;
        if (basename) {
            return [
                {
                    path: `/${basename}`,
                    component: VueEmpty,
                    children: [
                        {
                            isReact: true,
                            path: '',
                            component: this.pager,
                        },
                        {
                            isReact: true,
                            path: '*',
                            component: this.pager,
                        },
                    ],
                },
            ];
        }
        const routes = this.loopRoutes(this.configs);
        return routes;
    }
}

export { NoVueEnv, ReactAdapter, ReactAdapter as ReactPageAdapter, ReactRender, ReactRouterFactory, Reactive, VueAdapter, RouterFactory as VueRouterFactory, VueEmpty as VueRouterView, getReactRouterComponent, useVuexDispatch, useVuexGetters, useVuexState };
